import axios from 'axios';

// TMDB API Bearer Token (v4 authentication) - must be set in environment
const TMDB_BEARER_TOKEN = process.env.REACT_APP_TMDB_BEARER_TOKEN;

// Validate token is present
if (!TMDB_BEARER_TOKEN) {
  console.error('REACT_APP_TMDB_BEARER_TOKEN is not set in environment variables');
}

// Legacy API keys (for fallback rate limiting rotation)
const TMDB_API_KEYS = [
  process.env.REACT_APP_TMDB_API_KEY_1,
  process.env.REACT_APP_TMDB_API_KEY_2
].filter(Boolean);

let currentKeyIndex = 0;

const getApiKey = () => {
  return TMDB_API_KEYS[currentKeyIndex];
};

const rotateApiKey = () => {
  if (TMDB_API_KEYS.length > 1) {
    currentKeyIndex = (currentKeyIndex + 1) % TMDB_API_KEYS.length;
    console.log('Rotated to API key index:', currentKeyIndex);
  }
};

const BASE_URL = 'https://api.themoviedb.org/3';
const IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';

export const imageSize = {
  backdrop: '/original',
  poster: '/w500',
  small: '/w200'
};

export const getImageUrl = (path, size = imageSize.poster) => {
  if (!path) return '';
  const sizeStr = size.startsWith('/') ? size : `/${size}`;
  return `${IMAGE_BASE_URL}${sizeStr}${path}`;
};

// Create axios instance with Bearer token authentication
const tmdbApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Authorization': `Bearer ${TMDB_BEARER_TOKEN}`,
    'Content-Type': 'application/json'
  }
});

tmdbApi.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 429) {
      rotateApiKey();
    }
    return Promise.reject(error);
  }
);

export const fetchTrending = async (mediaType = 'all', timeWindow = 'week') => {
  try {
    const response = await tmdbApi.get(`/trending/${mediaType}/${timeWindow}`);
    return response.data.results;
  } catch (error) {
    console.error('Error fetching trending:', error);
    return [];
  }
};

export const fetchMoviesByCategory = async (category) => {
  try {
    const response = await tmdbApi.get(`/movie/${category}`);
    return response.data.results;
  } catch (error) {
    console.error(`Error fetching ${category} movies:`, error);
    return [];
  }
};

export const fetchTVByCategory = async (category) => {
  try {
    const response = await tmdbApi.get(`/tv/${category}`);
    return response.data.results;
  } catch (error) {
    console.error(`Error fetching ${category} TV shows:`, error);
    return [];
  }
};

export const fetchMoviesByGenre = async (genreId) => {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: {
        with_genres: genreId
      }
    });
    return response.data.results;
  } catch (error) {
    console.error(`Error fetching movies by genre ${genreId}:`, error);
    return [];
  }
};

export const fetchMovieDetails = async (movieId) => {
  try {
    const response = await tmdbApi.get(`/movie/${movieId}`, {
      params: {
        append_to_response: 'videos,credits'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching movie details:', error);
    return null;
  }
};

export const fetchTVDetails = async (tvId) => {
  try {
    const response = await tmdbApi.get(`/tv/${tvId}`, {
      params: {
        append_to_response: 'videos,credits'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching TV details:', error);
    return null;
  }
};

export const searchMulti = async (query) => {
  try {
    const response = await tmdbApi.get('/search/multi', {
      params: {
        query: query
      }
    });
    return response.data.results;
  } catch (error) {
    console.error('Error searching:', error);
    return [];
  }
};

export const getTrailerKey = (videos) => {
  if (!videos || !videos.results || videos.results.length === 0) return null;
  
  const trailer = videos.results.find(
    video => video.type === 'Trailer' && video.site === 'YouTube'
  );
  
  return trailer ? trailer.key : videos.results[0]?.key;
};

// Fetch all TV Shows (multiple categories combined)
export const fetchAllTVShows = async () => {
  try {
    const [popular, topRated, airingToday, onTheAir] = await Promise.all([
      tmdbApi.get('/tv/popular'),
      tmdbApi.get('/tv/top_rated'),
      tmdbApi.get('/tv/airing_today'),
      tmdbApi.get('/tv/on_the_air')
    ]);
    
    // Combine and deduplicate by ID
    const allShows = [
      ...popular.data.results,
      ...topRated.data.results,
      ...airingToday.data.results,
      ...onTheAir.data.results
    ];
    
    const uniqueShows = allShows.reduce((acc, show) => {
      if (!acc.find(s => s.id === show.id)) {
        acc.push({ ...show, media_type: 'tv' });
      }
      return acc;
    }, []);
    
    return uniqueShows;
  } catch (error) {
    console.error('Error fetching TV shows:', error);
    return [];
  }
};

// Fetch all Movies (multiple categories combined)
export const fetchAllMovies = async () => {
  try {
    const [popular, topRated, nowPlaying, upcoming] = await Promise.all([
      tmdbApi.get('/movie/popular'),
      tmdbApi.get('/movie/top_rated'),
      tmdbApi.get('/movie/now_playing'),
      tmdbApi.get('/movie/upcoming')
    ]);
    
    // Combine and deduplicate by ID
    const allMovies = [
      ...popular.data.results,
      ...topRated.data.results,
      ...nowPlaying.data.results,
      ...upcoming.data.results
    ];
    
    const uniqueMovies = allMovies.reduce((acc, movie) => {
      if (!acc.find(m => m.id === movie.id)) {
        acc.push({ ...movie, media_type: 'movie' });
      }
      return acc;
    }, []);
    
    return uniqueMovies;
  } catch (error) {
    console.error('Error fetching movies:', error);
    return [];
  }
};

// Fetch New & Popular (content from last 6 months)
export const fetchNewAndPopular = async () => {
  try {
    // Calculate date 6 months ago
    const today = new Date();
    const sixMonthsAgo = new Date(today.setMonth(today.getMonth() - 6));
    const fromDate = sixMonthsAgo.toISOString().split('T')[0];
    const toDate = new Date().toISOString().split('T')[0];
    
    const [movies, tvShows] = await Promise.all([
      tmdbApi.get('/discover/movie', {
        params: {
          sort_by: 'popularity.desc',
          'primary_release_date.gte': fromDate,
          'primary_release_date.lte': toDate
        }
      }),
      tmdbApi.get('/discover/tv', {
        params: {
          sort_by: 'popularity.desc',
          'first_air_date.gte': fromDate,
          'first_air_date.lte': toDate
        }
      })
    ]);
    
    // Combine movies and TV shows, sorted by popularity
    const combined = [
      ...movies.data.results.map(m => ({ ...m, media_type: 'movie' })),
      ...tvShows.data.results.map(t => ({ ...t, media_type: 'tv' }))
    ].sort((a, b) => b.popularity - a.popularity);
    
    return combined;
  } catch (error) {
    console.error('Error fetching new and popular:', error);
    return [];
  }
};

// Fetch TV season details with episode list
export const fetchTVSeasonDetails = async (tvId, seasonNumber) => {
  try {
    const response = await tmdbApi.get(`/tv/${tvId}/season/${seasonNumber}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching season ${seasonNumber} for TV ${tvId}:`, error);
    return null;
  }
};

// Fetch details for multiple items (for My List)
export const fetchMultipleDetails = async (items) => {
  try {
    const details = await Promise.all(
      items.map(async (item) => {
        if (item.media_type === 'movie') {
          const response = await tmdbApi.get(`/movie/${item.id}`);
          return { ...response.data, media_type: 'movie' };
        } else {
          const response = await tmdbApi.get(`/tv/${item.id}`);
          return { ...response.data, media_type: 'tv' };
        }
      })
    );
    return details.filter(d => d !== null);
  } catch (error) {
    console.error('Error fetching multiple details:', error);
    return [];
  }
};